import { IDataFlowArguments, DataFlowColumnArgument, IUsedIdentifiers } from 'app/model/dataflow/dataflow-expression-utilities';
import { ModelMarkerVisitor } from 'app/authoring/component/transformations/transformation-expression-builder/model.marker.visitor';
import { Antlr4Utils, ParsingVersion } from '../../../../model/adapters/dataflow/antlr4-utils';
import { AnyDataFlowType, IDataFlowDataType, NullDataFlowType } from '../../../../model/dataflow/dataflow-datatype';

import { format } from 'app/shared/utils/string.util';
import { DataFlowFunctionArgument } from 'app/model/dataflow/dataflow-function-argument';
import { ModelValidatorBase } from './model.validator.base';
import { RequiredFunctionValidator } from './model.requirefunction.validator';
import { DSLErrorCollector } from 'worker/app-workers/shared/models/DSLErrorCollector';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { Transformation } from 'app/model/dataflow/transformation';
import { IMarkerData, MarkerSeverity } from 'app/shared/components/monaco/monaco-models';
import { Interpolation } from 'worker/app-workers/shared/models/expressionTypes';

export class ModelMarkerProvider {
    protected static className: string = 'ModelMarkerProvider';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _evaluatedExpression: any;
    private _modelMarkerVisitor: ModelMarkerVisitor;

    constructor(private readonly _availableDataFlowArguments: IDataFlowArguments, private readonly _requiredFunctions?: DataFlowFunctionArgument[]) {
    }

    public evaluate(value: string): void {
        if (!value) {
            return;
        }
        const errorCollector = new DSLErrorCollector();

        // there would be too many models generated by this directly using antlr4 token offset, which is not ideal
        // thus we have to use the original parser w/o annotations to ensure their functionalities
        const tokenStream = Antlr4Utils.getTokenStream(value, errorCollector, ParsingVersion.OriginalVersion);
        const expression = Antlr4Utils.getExpression(tokenStream, errorCollector, ParsingVersion.OriginalVersion);

        const validators: ModelValidatorBase[] = [];
        if (this._requiredFunctions) {
            validators.push(new RequiredFunctionValidator(this._requiredFunctions));
        }
        this._modelMarkerVisitor = new ModelMarkerVisitor(tokenStream, this._availableDataFlowArguments, validators);
        this._evaluatedExpression = this._modelMarkerVisitor.visit(expression);

        const dataflowParser = Antlr4Utils.getParserDefinition();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const interestingTokens = tokenStream.tokens.filter((token: any) => token.type !== dataflowParser.DataflowParser.WS && token.type !== -1);

        if (this._evaluatedExpression && this._evaluatedExpression.returnType instanceof NullDataFlowType) {
            const lastToken = tokenStream.tokens[tokenStream.tokens.length - 1];
            this._modelMarkerVisitor.modelMarkers.push({
                severity: MarkerSeverity.Error,
                message: stringResources.DataFlowEditorErrorNullValue,
                startLineNumber: 0,
                startColumn: 0,
                endLineNumber: lastToken.line,
                endColumn: lastToken.start + 1
            });
        }

        validators.forEach((validator) => {
            const invalidExpression = validator.getInvalidExpression(this._evaluatedExpression);
            if (invalidExpression) {
                this._modelMarkerVisitor.modelMarkers.push({
                    severity: MarkerSeverity.Error,
                    message: validator.getErrorMessage(invalidExpression),
                    startLineNumber: invalidExpression.startPosition.line,
                    startColumn: invalidExpression.startPosition.column + 1,
                    endLineNumber: invalidExpression.stopPosition.line,
                    endColumn: invalidExpression.stopPosition.column + 2
                });
            }
        });

        const exception = this.getException(expression);

        if (value && exception) {
            const stopToken = exception.input.tokens[exception.input.tokens.length - 1];
            this._modelMarkerVisitor.modelMarkers.push({
                severity: MarkerSeverity.Error,
                message: stringResources.DataFlowEditorErrorUnableToParse_Generic,
                startLineNumber: expression.start.line,
                startColumn: expression.start.column + 1,
                endLineNumber: stopToken.line,
                endColumn: stopToken.start + 1
            });
        } else if (interestingTokens.length > 0 && interestingTokens[interestingTokens.length - 1] !== expression.stop) {
            this._modelMarkerVisitor.modelMarkers.push({
                severity: MarkerSeverity.Error,
                message: format(stringResources.DataFlowEditorErrorUnableToParse, value.substring(expression.stop.stop)),
                startLineNumber: expression.stop.line,
                startColumn: expression.stop.stop + 2,
                endLineNumber: interestingTokens[interestingTokens.length - 1].line,
                endColumn: interestingTokens[interestingTokens.length - 1].stop + 2
            });
        } else if (errorCollector.errors.length > 0) {
            errorCollector.errors.forEach((error) => {
                this._modelMarkerVisitor.modelMarkers.push({
                    severity: MarkerSeverity.Error,
                    message: error.msg,
                    startLineNumber: error.line,
                    startColumn: error.column + 1,
                    endLineNumber: error.line,
                    endColumn: error.column + 2
                });
            });
        }
    }

    public async asyncEvaluate(value: string, dataFlowContext: DataFlow, transformationContext: Transformation): Promise<void> {
        this.evaluate(value);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getException(expression: any): any {
        if (!expression) {
            return undefined;
        }

        if (expression.exception) {
            return expression.exception;
        }

        if (expression.children) {
            for (const child of expression.children) {
                const exception = this.getException(child);
                if (exception) {
                    return exception;
                }
            }
        }

        return undefined;
    }

    public getModelMarkers(): IMarkerData[] {
        return this._modelMarkerVisitor ? this._modelMarkerVisitor.modelMarkers : [];
    }

    public getUsedColumns(): DataFlowColumnArgument[] {
        return this._modelMarkerVisitor ? [...new Set(this._modelMarkerVisitor.usedIdentifiers.dataFlowColumns.map((dataFlowColumn) => dataFlowColumn.column))] : [];
    }

    public getUsedIdentifiers(): IUsedIdentifiers {
        return this._modelMarkerVisitor
            ? this._modelMarkerVisitor.usedIdentifiers
            : {
                dataFlowColumns: [],
                dataFlowFunctions: [],
                dataFlowParameters: [],
                dataFlowCacheSinks: []
            };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getInterpolations(): any[] {
        if (!this._evaluatedExpression) {
            return undefined;
        }

        const interpolations = this._evaluatedExpression.interpolation as Interpolation;
        const interpolatedExpressions = (interpolations && interpolations.expressions) ? interpolations.expressions.map((expr) => this._modelMarkerVisitor.visit(expr)) : [];

        return interpolatedExpressions;
    }

    public getExpressionReturnType(): IDataFlowDataType {
        return this._evaluatedExpression && this._evaluatedExpression.returnType ? this._evaluatedExpression.returnType : new AnyDataFlowType();
    }

    public isEvaluatedProperly(): boolean {
        return !!this._evaluatedExpression;
    }

    public hasError(): boolean {
        return !this.isEvaluatedProperly() || this.getErrors().length > 0;
    }

    public getErrors(): IMarkerData[] {
        return this._modelMarkerVisitor
            ? this._modelMarkerVisitor.modelMarkers.filter((modelMarker) => modelMarker.severity === MarkerSeverity.Error)
            : [];
    }

    public getWarnings(): IMarkerData[] {
        return this._modelMarkerVisitor
            ? this._modelMarkerVisitor.modelMarkers.filter((modelMarker) => modelMarker.severity === MarkerSeverity.Warning)
            : [];
    }
}
